import React, { useEffect, useMemo, useState } from "react";
import { ModalHeader, ModalShell } from "components/ui/ModalFrame";
import Button from "components/ui/Button";
import Input from "components/ui/Input";
import LoadingMark from "components/ui/LoadingMark";
import { MODAL_LAYER } from "constants/modalLayers";
import { MODAL_STICKY_FOOTER_END } from "constants/layoutSpacing";
import contractService from "services/contractService";
import { cn } from "utils/cn";

function getDocumentTitle(doc) {
  if (typeof doc?.title === "string" && doc.title.trim()) return doc.title.trim();
  if (doc?.title != null) return String(doc.title);
  return "Sem título";
}

function formatCreatedAt(iso) {
  if (!iso || typeof iso !== "string") return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

/** Every word in the filter must appear in the title (case-insensitive). */
function titleMatchesFilter(title, filterRaw) {
  const t = title.toLowerCase();
  const parts = filterRaw.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return true;
  return parts.every((p) => t.includes(p));
}

const ImportDocumentDataModal = ({
  isOpen,
  onClose,
  /** Backend template key, e.g. compromisso_compra_venda */
  templateKey,
  onConfirmImport,
}) => {
  const [items, setItems] = useState([]);
  const [titleFilter, setTitleFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState(null);
  const [pendingDoc, setPendingDoc] = useState(null);

  const filteredItems = useMemo(() => {
    return items.filter((doc) =>
      titleMatchesFilter(getDocumentTitle(doc), titleFilter)
    );
  }, [items, titleFilter]);

  useEffect(() => {
    if (!isOpen) return;

    setItems([]);
    setTitleFilter("");
    setListError(null);
    setPendingDoc(null);

    if (!templateKey) {
      setListError("Modelo de documento inválido.");
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setListError(null);

      const result = await contractService.getDocumentsByTemplate(templateKey);

      if (cancelled) return;

      if (!result.success) {
        setListError(
          typeof result.error === "string"
            ? result.error
            : "Não foi possível carregar os documentos."
        );
        setItems([]);
        setLoading(false);
        return;
      }

      setItems(result.data?.items || []);
      setLoading(false);
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [isOpen, templateKey]);

  const handlePickDocument = (doc) => {
    setPendingDoc(doc);
  };

  const handleConfirmNo = () => {
    setPendingDoc(null);
  };

  const handleConfirmYes = () => {
    if (!pendingDoc) return;
    onConfirmImport(pendingDoc);
    setPendingDoc(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ModalShell
      zClass={MODAL_LAYER.standard}
      backdropClassName="bg-black/50"
      panelClassName={cn(
        "bg-card rounded-xl shadow-elevation-lg flex flex-col",
        "w-[min(100vw-1.5rem,34rem)] max-w-lg",
        "max-h-[min(85vh,560px)]"
      )}
    >
      <ModalHeader
        title={
          pendingDoc
            ? "Confirmar importação"
            : "Importar dados de outro documento"
        }
        onClose={onClose}
      />

      <div className="flex-1 min-h-0 flex flex-col border-t border-border">
        {pendingDoc ? (
          <div className="p-6 space-y-4">
            <p className="text-sm text-foreground">
              Você confirma importar os dados do documento?
            </p>
            {pendingDoc.title ? (
              <p className="text-sm text-muted-foreground rounded-md border border-border bg-muted/30 px-3 py-2">
                <span className="font-medium text-foreground">
                  {pendingDoc.title}
                </span>
              </p>
            ) : null}
          </div>
        ) : (
          <div className="flex-1 min-h-0 flex flex-col gap-3 px-4 py-3">
            {loading && (
              <div className="flex justify-center py-6">
                <LoadingMark size="xs" message="Carregando documentos…" />
              </div>
            )}

            {!loading && listError && (
              <p className="text-sm text-destructive">{listError}</p>
            )}

            {!loading && !listError && items.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-6">
                Nenhum documento encontrado para este modelo.
              </p>
            )}

            {!loading && !listError && items.length > 0 && (
              <>
                <Input
                  label="Filtrar por título"
                  placeholder="Digite para filtrar…"
                  value={titleFilter}
                  onChange={(e) => setTitleFilter(e.target.value)}
                  autoComplete="off"
                />

                {filteredItems.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">
                    Nenhum documento corresponde ao filtro.
                  </p>
                ) : (
                  <ul
                    className={cn(
                      "flex flex-col gap-2 min-h-0 overflow-y-auto pr-0.5",
                      "max-h-[min(42vh,360px)]"
                    )}
                  >
                    {filteredItems.map((doc, i) => {
                      const key =
                        doc.job_id ||
                        doc.jobId ||
                        doc.s3_key ||
                        doc.s3Key ||
                        `doc-${i}`;
                      const title = getDocumentTitle(doc);
                      const created =
                        formatCreatedAt(doc.created_at) ||
                        formatCreatedAt(doc.createdAt);

                      return (
                        <li key={String(key)}>
                          <button
                            type="button"
                            onClick={() => handlePickDocument(doc)}
                            className={cn(
                              "group w-full text-left rounded-xl border-2 border-border/80 bg-card",
                              "px-4 py-3.5 min-h-[3.25rem]",
                              "transition-all duration-200 ease-out",
                              "cursor-pointer",
                              "hover:border-primary/50 hover:bg-muted/80 hover:shadow-md",
                              "hover:-translate-y-px",
                              "active:translate-y-0 active:shadow-sm active:bg-muted",
                              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                            )}
                          >
                            <span className="block font-medium text-foreground text-[15px] leading-snug line-clamp-2 group-hover:text-foreground">
                              {title}
                            </span>
                            {created ? (
                              <span className="mt-1.5 block text-xs text-muted-foreground tabular-nums">
                                Criado em {created}
                              </span>
                            ) : null}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <div
        className={cn(
          MODAL_STICKY_FOOTER_END,
          "flex-shrink-0 border-t border-border bg-card rounded-b-xl"
        )}
      >
        {pendingDoc ? (
          <>
            <Button type="button" variant="outline" onClick={handleConfirmNo}>
              Não
            </Button>
            <Button type="button" onClick={handleConfirmYes}>
              Sim
            </Button>
          </>
        ) : (
          <Button type="button" variant="outline" onClick={onClose}>
            Fechar
          </Button>
        )}
      </div>
    </ModalShell>
  );
};

export default ImportDocumentDataModal;
